import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  CircularProgress,
  AppBar
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import PolicyIcon from '@material-ui/icons/Policy'
import { Form, Formik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import Breadcrumbs from '../../components/Breadcrumb'
import PageHeader from '../../components/PageHeader'
import SectionTitle from '../../components/SectionTitle'
import apolloClient from '../../graphql/apolloClient'
import {
  CANCEL_POLICY_GENERATE,
  CANCEL_POLICY_MODIFY,
  CANCEL_POLICY_QUERY
} from '../../graphql/cancelPolicies'
import { getTranslate, lang } from '../../localization'
import { CancelPolicy, PolicyTable } from '../../types/cancelAndModifyPolicies'
import { Multilingual } from '../../types/roomtype'
import PoliciesFormAddPoliciesModal from './PoliciesFormAddPoliciesModal'
import PoliciesFormAvailablePoliciesList from './PoliciesFormAvailablePoliciesList'
import PoliciesFormInputBox from './PoliciesFormInputBox'
import { sortPolicies } from './policiesSorter'
import useStyles from './styles/PoliciesFormMain.styles'
import { CancelPolicyFormValidationSchema } from './validations'

const CANCELATION_POLICY_FORM_INIT_VALUES: CancelPolicy = {
  basicDetails: {
    name: '',
    description: '',
    descriptionTranslatedList: [
      { label: 'Iran, Islamic Republic of', value: '', nativeLabel: 'فارسی' }
    ],
    id: ''
  },
  table: []
}

const PoliciesForm = () => {
  const classes = useStyles()
  const history = useHistory()
  const { providerCode, id } = useParams<{
    providerCode: string
    id: string
  }>()

  const [getInitialValues, { loading: loadingForData }] = useLazyQuery<{
    cancelPolicy: CancelPolicy
  }>(CANCEL_POLICY_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  })

  const [cancelPolicyOnSubmitFunction, { loading: loadingForCancelPolicy }] =
    useMutation(id ? CANCEL_POLICY_MODIFY : CANCEL_POLICY_GENERATE, {
      notifyOnNetworkStatusChange: true
    })

  const [descriptionMultiLingual, setDescriptionMultiLingual] = useState<
    Multilingual[]
  >([])

  const [initialValues, setInitialValues] = useState<CancelPolicy>(
    CANCELATION_POLICY_FORM_INIT_VALUES
  )
  const [providerCodeState, setProviderCodeState] =
    useState<string>(providerCode)

  const getInitialValuesFunction = async () => {
    const { data } = await getInitialValues({
      variables: {
        providerCode: providerCodeState,
        policyID: id
      }
    })

    const initValues = data?.cancelPolicy!

    setInitialValues(initValues)

    if (initValues.basicDetails.descriptionTranslatedList.length > 0) {
      setDescriptionMultiLingual(
        initValues.basicDetails.descriptionTranslatedList
      )
    }
  }

  const handleSubmit = async (values: CancelPolicy) => {
    const tempTable: PolicyTable[] = []
    values.table.forEach((t) => {
      const singleTableRow: PolicyTable = {
        deadline: t.deadline,
        deadlineTimeOffset: t.deadlineTimeOffset,
        penaltyAmount: t.penaltyAmount,
        penaltyType: t.penaltyType,
        policyType: t.policyType,
        priority: t.priority
      }

      tempTable.push(singleTableRow)
    })

    try {
      const { data: response } = await cancelPolicyOnSubmitFunction({
        variables: {
          input: {
            table: tempTable,
            basicDetails: {
              ...values.basicDetails,
              descriptionTranslatedList: descriptionMultiLingual
            }
          },
          providerCode: providerCode ? providerCode : providerCodeState
        }
      })

      if (id) {
        toast.success(response.modifyCancelPolicy.message)
      } else {
        toast.success(response.generateCancelPolicy.message)
      }

      apolloClient.cache.evict({
        fieldName: 'cancelPolicies'
      })
      history.push(`/${lang}/policies/cancel-policies`)
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('ERROR!')
      }
    }
  }
  const [policiesModalIsOpen, setpoliciesModalIsOpen] = useState<boolean>(false)
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    if (id) {
      getInitialValuesFunction()
    }
  }, [])

  const cachedLevels = useMemo(
    () => [
      {
        label: getTranslate('Cancel Policies'),
        link: '/policies/cancel-policies'
      },
      {
        label: initialValues.basicDetails.name
      }
    ],
    [initialValues]
  )

  return (
    <div className={classes.root}>
      <PageHeader title={getTranslate('Cancel Policies')} />
      {id && (
        <AppBar
          className={classes.appBar}
          position="static"
          color="transparent">
          <Breadcrumbs levels={cachedLevels} loading={loadingForData} />
        </AppBar>
      )}
      <div className={classes.contentContainer}>
        <div className={classes.header}>
          <PolicyIcon fontSize="large" />
          <Typography className={classes.title} variant="h3" gutterBottom>
            {getTranslate(
              id ? initialValues.basicDetails.name : 'Add New Policies'
            )}
          </Typography>
        </div>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={CancelPolicyFormValidationSchema}>
          {({
            values,
            setFieldValue,
            handleBlur,
            isSubmitting,
            errors,
            touched,
            isValid
          }) => {
            return (
              <Form>
                <div>
                  <div className={classes.titleContainer}>
                    <SectionTitle title="Basic Details" marginBottom />
                    {loadingForData ? <CircularProgress /> : null}{' '}
                  </div>
                  <PoliciesFormInputBox
                    {...{
                      values,
                      setFieldValue,
                      handleBlur,
                      isSubmitting,
                      errors,
                      touched,
                      classes,
                      loadingForData
                    }}
                    providerCodeState={providerCodeState}
                    setProviderCodeState={setProviderCodeState}
                    descriptionMultiLingual={descriptionMultiLingual}
                    setDescriptionMultiLingual={setDescriptionMultiLingual}
                  />
                  <div className={classes.tableHeaderContainer}>
                    <SectionTitle title="Policies" marginBottom />
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.button}
                      size="medium"
                      fullWidth={matches}
                      startIcon={<AddIcon />}
                      onClick={() => setpoliciesModalIsOpen(true)}>
                      {getTranslate('Add Policies')}
                    </Button>
                  </div>
                  <PoliciesFormAddPoliciesModal
                    {...{
                      values,
                      setFieldValue,
                      classes
                    }}
                    isOpen={policiesModalIsOpen}
                    onClose={() => setpoliciesModalIsOpen(false)}
                  />
                  <div>
                    {sortPolicies(values.table) ? (
                      <PoliciesFormAvailablePoliciesList
                        onDelete={(data: PolicyTable) => {
                          var temp = [...values.table].filter(
                            (item) => item !== data
                          )
                          setFieldValue('table', temp)
                        }}
                        policiesData={sortPolicies(values.table)}
                      />
                    ) : (
                      <Typography variant="h4" className={classes.noPilocies}>
                        {getTranslate('No Policies')}
                      </Typography>
                    )}
                  </div>
                  <div>
                    <div className={classes.footer}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!isValid || loadingForCancelPolicy}
                        size="medium"
                        fullWidth={matches}>
                        {getTranslate('Save Policies')}
                        {loadingForCancelPolicy && (
                          <CircularProgress
                            className={classes.circularProgress}
                            size={16}
                          />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </Form>
            )
          }}
        </Formik>
      </div>
    </div>
  )
}
export default PoliciesForm
